import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import ZoneDeChamp from './ZoneDeChamp';

/**
 * UNE PAGE DU GABARIT, EN IMAGE, ET LES ZONES POSÉES DESSUS.
 *
 * Le conteneur est en `position: relative` et l'image en largeur 100 % : les zones se
 * posent dessus en `position: absolute` avec des POURCENTAGES, sans jamais convertir de
 * pixels.
 *
 * ⚠️ `ratio` VIENT DU SERVEUR, PAS DE L'IMAGE. Réserver la place avant que l'image
 * n'arrive évite que la pile de pages ne saute au chargement — et sur un écran où l'on
 * pose des champs au pixel, un saut de mise en page pendant qu'on tient le bouton de la
 * souris déplacerait le champ.
 *
 * ⚠️ ON MESURE L'IMAGE, PAS LE CONTENEUR. Le conteneur porte une bordure et un rayon :
 * deux ou trois pixels d'écart suffisent à décaler toutes les zones d'un demi-millimètre
 * sur le PDF produit. `getBoundingClientRect()` de l'`<img>` est la SEULE mesure prise de
 * tout l'atelier côté écran.
 *
 * ⚠️ ET ELLE SERT DEUX FOIS. La même mesure donne le rapport POINTS → PIXELS, qui permet
 * d'écrire la valeur d'exemple d'une zone à la taille qu'elle aura vraiment sur le
 * document. Sans lui, l'écran montrerait un texte à une taille arbitraire et l'opérateur
 * ne saurait toujours pas si sa valeur tient dans le cadre qu'il vient de tracer. On
 * l'écoute avec un `ResizeObserver` : le panneau est redimensionnable, et une échelle
 * figée au chargement mentirait dès le premier déplacement de séparateur.
 */
export default function PageDeGabarit({
  page, image, zones = [], libelles = {}, exemples = {}, selection, placement, zoom = 1,
}) {
  const img = useRef(null);
  const [ptEnPx, setPtEnPx] = useState(null);
  const mesurer = useCallback(() => img.current?.getBoundingClientRect(), []);

  // Le rapport points PDF → pixels CSS. Il ne sert QU'À AFFICHER : aucune zone n'est
  // enregistrée à partir de lui, la conversion vers le PDF vit côté serveur.
  useEffect(() => {
    const noeud = img.current;
    if (!noeud || !page.hauteur_pt) return undefined;
    const relever = () => {
      const cadre = noeud.getBoundingClientRect();
      if (cadre.height) setPtEnPx(cadre.height / page.hauteur_pt);
    };
    relever();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observateur = new ResizeObserver(relever);
    observateur.observe(noeud);
    return () => observateur.disconnect();
  }, [image, page.hauteur_pt]);

  // Un clic sur le fond désélectionne : sans ça, la zone choisie garde ses poignées et
  // les flèches du clavier continuent de la déplacer alors qu'on croit avoir lâché.
  const surLeFond = (e) => {
    if (e.target === img.current) placement?.setSelection?.(null);
  };

  // Les lignes du document, montrées PENDANT le geste et pas avant.
  //
  // ⚠️ L'AIMANT EXISTAIT SANS SE VOIR, et c'est ce qui le rendait déroutant : la zone
  // sautait de quelques pixels sans qu'on sache ni pourquoi ni vers quoi. Les afficher
  // en permanence quadrillerait le document ; les afficher pendant le déplacement montre
  // exactement les lignes auxquelles la base peut se coller.
  const enDeplacement = placement?.enDeplacement;
  const zonesDeLaPage = zones.filter((z) => z.page === page.numero);
  const lignes = enDeplacement
    && zonesDeLaPage.some((z) => z.id === enDeplacement)
    ? (page.lignes_y || []) : [];

  return (
    <div
      // ⚠️ LA HAUTEUR COMMANDE, PAS LA LARGEUR. La page prenait toute la largeur de la
      // colonne et débordait donc en hauteur : sur une A4, on ne voyait jamais le document
      // en entier, et Boubacar l'a lu comme « l'aperçu est tronqué » (09/09). On cale
      // désormais sur la hauteur disponible, et `aspectRatio` en déduit la largeur.
      // ⚠️ LE ZOOM CHANGE LA TAILLE RÉELLE, IL N'APPLIQUE PAS UN `transform`.
      //
      // Un `scale()` grossit à l'écran sans agrandir la boîte : le conteneur ne sait donc
      // pas qu'il faut défiler, et la moitié de la page devient inatteignable. En posant
      // une LARGEUR, l'élément grandit pour de vrai, `aspectRatio` en déduit la hauteur,
      // et le défilement vient tout seul.
      //
      // Les zones suivent sans une ligne de code : elles sont en POURCENTAGES, et le
      // calcul du glisser passe par `getBoundingClientRect()` de l'image, qui rend la
      // boîte telle qu'elle est affichée. C'est la règle d'ATL-2 qui paie ici.
      className={`relative bg-white border border-neutral-200 rounded-md overflow-hidden
                  shrink-0 ${zoom === 1 ? 'h-full w-auto max-w-full' : ''}`}
      style={{
        aspectRatio: `${page.largeur_pt} / ${page.hauteur_pt}`,
        ...(zoom === 1 ? {} : { width: `${zoom * 100}%`, height: 'auto' }),
      }}
      onPointerMove={placement?.deplacer}
      onPointerUp={placement?.finir}
      onPointerDown={surLeFond}
      data-page-numero={page.numero}
      data-testid={`gabarit-plan-page-${page.numero}`}
    >
      {image ? (
        <img
          ref={img}
          src={image}
          alt={`Page ${page.numero + 1} du modèle de mandat`}
          className="w-full h-full block select-none"
          draggable={false}
          data-testid={`gabarit-page-${page.numero}`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-[12px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> page {page.numero + 1}…
        </div>
      )}

      {lignes.map((y) => (
        <span
          key={y}
          className="absolute left-0 right-0 border-t border-dashed border-marque/40
                     pointer-events-none z-0"
          style={{ top: `${y * 100}%` }}
          data-testid="gabarit-ligne-aimant"
        />
      ))}

      {zonesDeLaPage.map((zone) => (
        <ZoneDeChamp
          key={zone.id}
          zone={zone}
          libelle={libelles[zone.champ] || zone.champ}
          exemple={exemples[zone.champ]}
          ptEnPx={ptEnPx}
          choisie={selection === zone.id}
          mesurer={mesurer}
          onCommencer={placement.commencer}
          onSupprimer={placement.supprimer}
          onChoisir={placement.setSelection}
        />
      ))}

      <span className="absolute bottom-1 right-2 text-[10px] text-neutral-400 bg-white/80 px-1 rounded
                       pointer-events-none">
        {page.numero + 1} / {page.total}
      </span>
    </div>
  );
}
